import { TaskListItemTypes } from '@griply/types';
import { resolveGoalRowLink } from '@griply/utils';
import { ItemInstance, TreeInstance } from '@headless-tree/core';
import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import React from 'react';
import { GoalListCard } from 'shared/components/ui/goal-list-card';
import {
  InternalTreeItem,
  useDragPreview,
} from 'shared/components/ui/virtualized-tree-list';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { ContextMenuOptions } from 'shared/views/goal-filter-list/types';

import { useContextMenu } from '../../context-menu-context';
import * as Styled from './tree-goal-card.style';

type TreeGoalCardProps = {
  goal: Goal;
  item: ItemInstance<InternalTreeItem>;
  tree: TreeInstance<InternalTreeItem>;
  isMultiSelectModifierPressed?: boolean;
  onUpdateProgress: (id: string) => void;
  hideLifeArea?: boolean;
  showParentGoalLabel?: boolean;
  showParentLifeAreaLabel?: boolean;
  showParentLabelsForGoals?: boolean;
  excludedContextMenuOptions?: ContextMenuOptions[];
  // This list's sections are life areas, so a section row names a life-area link.
  sectionsAreLifeAreas?: boolean;
  // The link this list is rooted in, if any — a root row hangs under it.
  rootLink?: { goalId?: ID; lifeAreaId?: ID };
};

export const TreeGoalCard: React.FC<TreeGoalCardProps> = ({
  goal,
  item,
  tree,
  isMultiSelectModifierPressed,
  onUpdateProgress,
  hideLifeArea,
  showParentGoalLabel,
  showParentLifeAreaLabel,
  showParentLabelsForGoals,
  excludedContextMenuOptions,
  sectionsAreLifeAreas,
  rootLink,
}) => {
  const goals = useGoals();
  const links = useEntityLinks();
  const lifeAreas = useLocalizedLifeAreas({ activeOnly: true });
  const isDragPreview = useDragPreview();
  const hasChildren = !!item.getItemData().children?.length;
  const isExpanded = item.isExpanded();
  const preventInteraction = isMultiSelectModifierPressed;
  const indentLevel = isDragPreview ? 0 : item.getItemMeta().level;
  const parentData = isDragPreview
    ? undefined
    : item.getParent()?.getItemData();
  // A row nested under a goal already shows that parent; the overline is for what the tree doesn't.
  const isNestedUnderGoal = parentData?.type === TaskListItemTypes.Goal;

  const sourceLink = resolveGoalRowLink({
    parent: parentData && { id: parentData.sourceId, type: parentData.type },
    sectionsAreLifeAreas,
    rootLink,
  });

  const { open } = useContextMenu();

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isExpanded) {
      item.collapse();
    } else {
      item.expand();
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const goalsMap = new Map(goals.map((g) => [g.id, g]));
    const selectedItems = [...tree.getSelectedItems()];
    const selectedGoals = selectedItems
      .map((i) => goalsMap.get(i.getItemData().sourceId))
      .filter(Boolean) as Goal[];

    if (!item.isSelected()) {
      tree.setSelectedItems([item.getId()]);
      selectedGoals.length = 0;
      selectedGoals.push(goal);
    }

    open(
      { left: e.clientX, top: e.clientY },
      {
        goals: selectedGoals,
      },
    );
  };

  const isMultiSelected =
    item.isSelected() && tree.getSelectedItems().length > 1;

  return (
    <Styled.Container
      onContextMenu={handleContextMenu}
      $indentLevel={indentLevel}
    >
      <Styled.CardWrapper>
        <GoalListCard
          goal={goal}
          goals={goals}
          lifeAreas={lifeAreas}
          hideLifeArea={hideLifeArea}
          showParentGoalLabel={showParentGoalLabel && !isNestedUnderGoal}
          showParentLifeAreaLabel={showParentLifeAreaLabel}
          showParentLabelsForGoals={showParentLabelsForGoals}
          links={links}
          hasChildren={hasChildren}
          isExpanded={isExpanded}
          onToggleExpand={handleToggle}
          isSelected={item.isSelected()}
          isDropTarget={item.isDragTarget()}
          preventInteraction={preventInteraction}
          onUpdateProgress={onUpdateProgress}
          hideContextMenu={isMultiSelected || isMultiSelectModifierPressed}
          excludedContextMenuOptions={excludedContextMenuOptions}
          sourceLink={sourceLink}
          indentLevel={indentLevel}
        />
      </Styled.CardWrapper>
    </Styled.Container>
  );
};
